import * as m from '@staiter/i18n';
import type { AnomalyType } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowDownToLine,
  BarChart3,
  Calendar,
  CircleSlash,
  Copy,
  CreditCard,
  Factory,
  HelpCircle,
  Layers,
  Scale,
  Trash2,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { ExportMenu, ListPageShell, PageHeader, SavedViewsBar, Table } from '~/components/data';
import { Button, Select } from '~/components/primitives';
import { StatusBadge } from '~/components/status';
import {
  type AnomalyFilter,
  bulkAnomalyAction,
  dismissAnomaly,
  fetchAnomalies,
  reviewAnomaly,
} from '~/lib/api/anomalies';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/anomalies')({
  staticData: { minMainWidth: 960 },
  component: AnomaliesInbox,
});

// FE-197 Pass 48: icon per anomaly type — visual differentiation на list view.
const TYPE_ICONS: Partial<Record<AnomalyType, JSX.Element>> = {
  qty_zscore_outlier: <BarChart3 size={14} />,
  valuation_rate_jump: <TrendingUp size={14} />,
  bom_yield_variance: <Factory size={14} />,
  cost_layer_imbalance: <Layers size={14} />,
  velocity_drop: <TrendingDown size={14} />,
  duplicate_voucher: <Copy size={14} />,
  orphan_movement: <HelpCircle size={14} />,
  overdue_ap: <Calendar size={14} />,
  overdue_ar: <Calendar size={14} />,
  reconciliation_variance: <Scale size={14} />,
  fx_rate_jump: <Wallet size={14} />,
  cash_flow_negative: <ArrowDownToLine size={14} />,
  scrap_above_threshold: <Trash2 size={14} />,
  negative_stock: <CircleSlash size={14} />,
  duplicate_payment_candidate: <CreditCard size={14} />,
};

function AnomaliesInbox(): JSX.Element {
  const [filter, setFilter] = useState<AnomalyFilter>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['anomalies', filter],
    queryFn: () => fetchAnomalies(filter),
  });
  const state = useListPageState(q);

  const items = q.data?.items ?? [];
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};

  const reviewMut = useMutation({
    mutationFn: (id: string) => reviewAnomaly(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anomalies'] }),
  });
  const dismissMut = useMutation({
    mutationFn: (id: string) => dismissAnomaly(id, 'manual dismiss'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anomalies'] }),
  });
  const bulkMut = useMutation({
    mutationFn: (action: 'review' | 'dismiss') => bulkAnomalyAction([...selected], action),
    onSuccess: () => {
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['anomalies'] });
    },
  });

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.anomalies_page_title()}
        actions={
          <ExportMenu resource="anomalies" filters={filter as Record<string, string | undefined>} />
        }
      />
      <SavedViewsBar
        resource="anomalies"
        currentFilters={filter}
        onApply={(v) => setFilter(v.filters as AnomalyFilter)}
      />
      <section className={shared.sectionCard}>
        <h3 className={shared.sectionHeading}>{m.anomalies_filters_heading()}</h3>
        <div className={shared.formGrid2}>
          <Select
            label={m.anomalies_severity_label()}
            value={filter.severity ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, severity: e.target.value || undefined }))}
            options={[
              { value: '', label: m.anomalies_filter_all() },
              { value: 'info', label: m.anomalies_severity_info() },
              { value: 'warn', label: m.anomalies_severity_warn() },
              { value: 'major', label: m.anomalies_severity_major() },
              { value: 'critical', label: m.anomalies_severity_critical() },
            ]}
          />
          <Select
            label={m.anomalies_type_label()}
            value={filter.type ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value || undefined }))}
            options={[
              { value: '', label: m.anomalies_filter_all() },
              { value: 'qty_zscore_outlier', label: m.anomalies_type_qty_outlier() },
              { value: 'valuation_rate_jump', label: m.anomalies_type_rate_jump() },
              { value: 'bom_yield_variance', label: m.anomalies_type_yield_variance() },
            ]}
          />
        </div>
        {/* FE-8: active filter chips для visible feedback. */}
        {(filter.severity || filter.type) && (
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              {m.ui_aktivnye_filtry()}
            </span>
            {filter.severity && (
              <button
                type="button"
                onClick={() => setFilter((f) => ({ ...f, severity: undefined }))}
                style={{
                  padding: '2px 8px',
                  background: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 999,
                  fontSize: 11,
                  cursor: 'pointer',
                }}
                aria-label="Удалить фильтр критичности"
              >
                Критичность: {filter.severity} ×
              </button>
            )}
            {filter.type && (
              <button
                type="button"
                onClick={() => setFilter((f) => ({ ...f, type: undefined }))}
                style={{
                  padding: '2px 8px',
                  background: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 999,
                  fontSize: 11,
                  cursor: 'pointer',
                }}
                aria-label="Удалить фильтр типа"
              >
                Тип: {filter.type} ×
              </button>
            )}
            <button
              type="button"
              onClick={() => setFilter({})}
              style={{
                padding: '2px 8px',
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-secondary)',
                fontSize: 11,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              aria-label="Сбросить все фильтры"
            >
              {m.ui_sbrosit_vse()}
            </button>
          </div>
        )}
      </section>

      {selected.size > 0 && (
        <div className={shared.filters} role="toolbar" aria-label={m.anomalies_bulk_aria()}>
          <span>{m.anomalies_bulk_selected({ count: selected.size })}</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => bulkMut.mutate('review')}
            loading={bulkMut.isPending}
            disabled={bulkMut.isPending}
          >
            {m.anomalies_bulk_review()}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => bulkMut.mutate('dismiss')}
            loading={bulkMut.isPending}
            disabled={bulkMut.isPending}
          >
            {m.anomalies_btn_dismiss()}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            {m.anomalies_bulk_clear()}
          </Button>
        </div>
      )}

      <ListPageShell
        state={state}
        emptyTitle={m.anomalies_empty_title()}
        emptyDescription={m.anomalies_empty_desc()}
      >
        {(rows) => (
          <Table
            ariaLabel={m.anomalies_page_title()}
            data={rows}
            rowKey={(a) => a.id}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            // FE-3: row-click navigates to evidence filtered by anomaly id.
            onRowClick={(a) => {
              window.location.href = `/evidence?anomaly_id=${a.id}`;
            }}
            columns={[
              {
                id: 'type',
                header: m.anomalies_col_type(),
                // FE-197 Pass 20+48: localize raw enum + width так что text visible
                // (раньше column shrunk до icon-only — text scrolled off).
                width: '220px',
                accessor: (a) => {
                  // FE-197 Pass 48: cover all 16 AnomalyType variants per shared/enums.ts.
                  const localized: Record<string, string> = {
                    qty_zscore_outlier: m.anomalies_type_qty_outlier(),
                    valuation_rate_jump: m.anomalies_type_rate_jump(),
                    bom_yield_variance: m.anomalies_type_yield_variance(),
                    cost_layer_imbalance: m.anomalies_type_cost_layer_imbalance(),
                    velocity_drop: m.anomalies_type_velocity_drop(),
                    duplicate_voucher: m.anomalies_type_duplicate_voucher(),
                    orphan_movement: m.anomalies_type_orphan_movement(),
                    overdue_ap: m.anomalies_type_overdue_ap(),
                    overdue_ar: m.anomalies_type_overdue_ar(),
                    reconciliation_variance: m.anomalies_type_reconciliation_variance(),
                    fx_rate_jump: m.anomalies_type_fx_rate_jump(),
                    cash_flow_negative: m.anomalies_type_cash_flow_negative(),
                    scrap_above_threshold: m.anomalies_type_scrap_above_threshold(),
                    negative_stock: m.anomalies_type_negative_stock(),
                    duplicate_payment_candidate: m.anomalies_type_duplicate_payment_candidate(),
                    counterparty_missing_bin: m.anomalies_type_counterparty_missing_bin(),
                    valuation_rate_missing: m.anomalies_type_valuation_rate_missing(),
                  };
                  const typeKey = a.type ?? a.anomaly_type ?? '';
                  return (
                    <span className={shared.rowCenter}>
                      {TYPE_ICONS[(a.type ?? a.anomaly_type) as AnomalyType] ?? (
                        <AlertTriangle size={14} />
                      )}
                      <span>{localized[typeKey] ?? typeKey}</span>
                    </span>
                  );
                },
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (a: (typeof rows)[number]) =>
                        (a.entity_id ? entities[a.entity_id]?.name : undefined) ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              {
                id: 'subject',
                header: m.anomalies_col_subject(),
                // FE-145 Sprint Q+ + FE-196 Pass 48: fallback chain — subject alias,
                // затем subject_ref (canonical BE field), последний UUID prefix.
                // FE-6: prettify composite subjects like 'payment_pair:DATE1_DATE2' →
                // 'payment_pair: DATE1 ↔ DATE2'.
                // FE-10: EntityChip pattern — split type:code into a chip.
                accessor: (a) => {
                  const s = a.subject ?? a.subject_ref;
                  if (!s) return <span className={shared.mono}>{a.id.slice(0, 8)}…</span>;
                  const colonIdx = s.indexOf(':');
                  if (colonIdx > 0) {
                    const type = s.slice(0, colonIdx);
                    let code = s.slice(colonIdx + 1);
                    // FE-6 composite payment_pair: 2025-11-12_2025-11-13 → 12.11.25 ↔ 13.11.25
                    if (type === 'payment_pair' && code.includes('_')) {
                      const [d1, d2] = code.split('_');
                      code = `${d1} ↔ ${d2}`;
                    }
                    return (
                      <span className={shared.rowCenter}>
                        <span
                          className={shared.mono}
                          style={{
                            fontSize: 11,
                            padding: '1px 6px',
                            background: 'var(--color-bg-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 4,
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {type}
                        </span>
                        <span className={shared.mono}>{code}</span>
                      </span>
                    );
                  }
                  return s;
                },
              },
              {
                id: 'sev',
                header: m.anomalies_col_severity(),
                // FE-143 Sprint N+: severity through StatusBadge primitive (color + icon).
                accessor: (a) => {
                  const map: Record<
                    string,
                    'needs_review' | 'degraded' | 'escalated' | 'completed_draft'
                  > = {
                    info: 'completed_draft',
                    warn: 'needs_review',
                    major: 'degraded',
                    critical: 'escalated',
                  };
                  const status = (a.severity ? map[a.severity] : undefined) ?? 'needs_review';
                  return <StatusBadge status={status} />;
                },
              },
              {
                id: 'status',
                header: m.anomalies_col_status(),
                // FE-144 Sprint N+: status through StatusBadge primitive.
                accessor: (a) => {
                  const map: Record<string, 'needs_review' | 'completed_draft' | 'escalated'> = {
                    open: 'needs_review',
                    reviewed: 'completed_draft',
                    escalated: 'escalated',
                  };
                  const status = (a.status ? map[a.status] : undefined) ?? 'needs_review';
                  return <StatusBadge status={status} />;
                },
              },
              {
                // FE-7: SLA age column — hours since detected_at.
                // FE-7 honest-audit: SLA color thresholds — >24ч warn, >7д danger.
                id: 'age',
                header: 'Возраст',
                width: '80px',
                mono: true,
                accessor: (a) => {
                  if (!a.detected_at) return '—';
                  const hours = Math.floor(
                    (Date.now() - new Date(a.detected_at).getTime()) / 3_600_000,
                  );
                  const days = Math.floor(hours / 24);
                  const label = hours < 24 ? `${hours}ч` : `${days}д`;
                  const color =
                    days > 7
                      ? 'var(--color-danger)'
                      : hours > 24
                        ? 'var(--color-warn)'
                        : 'var(--text-muted)';
                  const title =
                    days > 7
                      ? `Просрочено SLA: > 7 дней (${days}д)`
                      : hours > 24
                        ? `Внимание SLA: > 24ч (${days}д)`
                        : `${hours}ч с момента детекции`;
                  return (
                    <span style={{ color }} title={title}>
                      {label}
                    </span>
                  );
                },
              },
              {
                id: 'actions',
                header: '',
                accessor: (a) => (
                  <div className={shared.rowWrap}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => reviewMut.mutate(a.id)}
                      disabled={reviewMut.isPending}
                    >
                      {m.anomalies_btn_review()}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismissMut.mutate(a.id)}
                      disabled={dismissMut.isPending}
                    >
                      {m.anomalies_btn_dismiss()}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>
    </div>
  );
}
